import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  LogIn,
  LogOut,
  Languages,
  GaugeCircle,
  Sparkles,
} from "lucide-react";
import KioskLayout from "@/kiosk/KioskLayout";
import kioskApi, { clearKioskToken, getKioskToken } from "@/lib/kioskApi";

function useBranding() {
  const [branding, setBranding] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        // Fetch branding via heartbeat (server returns site_id in JWT — easier to call public-branding once we know site)
        // Use heartbeat to confirm token validity
        await kioskApi.post("/kiosks/heartbeat");
        // Branding via stored URL form: /sites/{site_id}/public-branding
        // Decode kiosk JWT (no signature check) to grab site_id for branding
        const tok = getKioskToken();
        if (!tok) return;
        const payload = JSON.parse(atob(tok.split(".")[1]));
        const { data } = await kioskApi.get(`/sites/${payload.site_id}/public-branding`);
        setBranding(data);
      } catch {
        /* unauth — fall through to landing logic */
      }
    })();
  }, []);
  return branding;
}

export default function KioskHome() {
  const nav = useNavigate();
  const { i18n, t } = useTranslation();
  const branding = useBranding();
  const [idle, setIdle] = useState(true);
  const idleTimer = useRef();
  const idleSeconds = (branding?.idle_lock_seconds || 90) * 1000;

  // Auth gate
  useEffect(() => {
    if (!getKioskToken()) {
      nav("/kiosk/pair", { replace: true });
    }
  }, [nav]);

  // Idle screensaver — bounce back to landing after configured timeout (we're already on landing)
  useEffect(() => {
    const reset = () => {
      setIdle(false);
      clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => setIdle(true), idleSeconds);
    };
    ["touchstart", "click", "keydown", "mousemove"].forEach((evt) =>
      window.addEventListener(evt, reset)
    );
    reset();
    return () => {
      ["touchstart", "click", "keydown", "mousemove"].forEach((evt) =>
        window.removeEventListener(evt, reset)
      );
      clearTimeout(idleTimer.current);
    };
  }, [idleSeconds]);

  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30 * 1000);
    return () => clearInterval(t);
  }, []);

  const goSignIn = () => nav("/kiosk/sign-in");
  const goSignOut = () => nav("/kiosk/sign-out");
  // Phase C1.5 — Module 7 Returning Visitor fast lane. Org toggle is
  // resolved server-side in `/api/kiosk/visitor-lookup` (returns 404 when
  // off); we still surface the tile so kiosk PWA caching doesn't show
  // stale state, but the lookup gives a clear error if disabled.
  const goWelcomeBack = () => nav("/kiosk/welcome-back");

  return (
    <KioskLayout branding={branding}>
      <div className="flex items-center justify-between text-sm text-white/70" data-testid="kiosk-home">
        <div className="flex items-center gap-2">
          {branding?.logo_url ? (
            <img src={branding.logo_url} alt="logo" className="h-8 max-w-[120px] object-contain" />
          ) : (
            <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
              <GaugeCircle className="w-5 h-5 text-orange-400" />
            </div>
          )}
          <div className="font-medium">{branding?.org_name || "EntryBook"}</div>
        </div>
        <div className="flex items-center gap-3">
          <span className="font-mono text-xs">{now.toLocaleString()}</span>
          <button
            onClick={() => clearKioskToken() || nav("/kiosk/pair", { replace: true })}
            className="text-[10px] uppercase tracking-wider text-white/40 hover:text-white/70"
            data-testid="kiosk-unpair-btn"
          >
            Unpair this device
          </button>
        </div>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center text-center">
        {idle ? (
          <button
            onClick={() => setIdle(false)}
            className="text-white/80 text-2xl uppercase tracking-[0.4em] hover:text-white transition-colors"
            data-testid="kiosk-tap-to-start"
          >
            {t("tap_to_start")}
          </button>
        ) : (
          <>
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight" data-testid="kiosk-welcome">
              {branding?.welcome_message || t("welcome")}
            </h1>
            <p className="mt-4 text-white/70 text-lg">{branding?.site_name}</p>

            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5 w-full max-w-2xl">
              <button
                onClick={goSignIn}
                data-testid="kiosk-sign-in-btn"
                className="group relative overflow-hidden rounded-2xl bg-white text-navy-900 px-8 py-10 hover:scale-[1.02] active:scale-[0.99] transition-transform"
                style={{ minHeight: 168 }}
              >
                <LogIn className="w-12 h-12 mb-3" style={{ color: "var(--kiosk-accent)" }} />
                <div className="text-3xl font-semibold">{t("sign_in")}</div>
                <ArrowRight className="absolute right-6 top-6 w-5 h-5 text-navy-400 group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={goSignOut}
                data-testid="kiosk-sign-out-btn"
                className="group relative overflow-hidden rounded-2xl bg-white/10 backdrop-blur border border-white/20 text-white px-8 py-10 hover:bg-white/15 active:scale-[0.99] transition-all"
                style={{ minHeight: 168 }}
              >
                <LogOut className="w-12 h-12 mb-3 text-orange-300" />
                <div className="text-3xl font-semibold">{t("sign_out")}</div>
                <ArrowRight className="absolute right-6 top-6 w-5 h-5 text-white/40 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>

            {/* Phase C1.5 — Returning Visitor fast lane (full-width tile so
                first-time visitors aren't distracted but returning visitors
                see it immediately). */}
            <button
              onClick={goWelcomeBack}
              data-testid="kiosk-welcome-back-btn"
              className="mt-5 group relative w-full max-w-2xl rounded-2xl bg-orange-500/15 hover:bg-orange-500/25 border-2 border-orange-400/40 text-white px-8 py-6 active:scale-[0.99] transition-all"
              style={{ minHeight: 96 }}
            >
              <div className="flex items-center justify-center gap-3">
                <Sparkles className="w-7 h-7 text-orange-300" />
                <div className="text-xl md:text-2xl font-semibold">I've been here before</div>
                <ArrowRight className="w-5 h-5 text-orange-200 group-hover:translate-x-1 transition-transform" />
              </div>
              <div className="mt-1 text-xs text-orange-100/80">
                Skip the long form — just type your email or phone.
              </div>
            </button>
          </>
        )}
      </div>

      <div className="flex items-center justify-center gap-4 text-xs text-white/50">
        <Languages className="w-4 h-4" />
        <button onClick={() => i18n.changeLanguage("en")} className={`uppercase ${i18n.language?.startsWith("en") ? "text-orange-300" : ""}`} data-testid="kiosk-lang-en">EN</button>
        <span>·</span>
        <button onClick={() => i18n.changeLanguage("de")} className={`uppercase ${i18n.language?.startsWith("de") ? "text-orange-300" : ""}`} data-testid="kiosk-lang-de">DE</button>
      </div>
    </KioskLayout>
  );
}
